/**
 * Fantasy Console 0 - Cart Library Tab
 *
 * Displays all saved carts from Supabase with search, sort, and management features.
 * Supports filtering by user and authenticated actions (like, fork).
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useCart } from '../../context/CartContext'
import { useToast } from '../../context/ToastContext'
import { useAuth } from '../../context/AuthContext'
import { useAuthManager } from '../Auth/AuthManager'
import { CartService, CartWithStats } from '../../services/cartService'
import { LikeService } from '../../services/likeService'
import { CartLibraryCache } from '../../services/cartLibraryCache'
import { CartCard } from './CartCard'
import { CartCardSkeleton } from './CartCardSkeleton'
import { EditCartModal } from './EditCartModal'
import { ConfirmDialog } from '../UI/ConfirmDialog'
import styles from './CartLibraryTab.module.css'
import { RefreshIcon } from '../shared/icons'

type SortBy = 'updated' | 'created' | 'title' | 'likes'
type FilterBy = 'all' | 'mine'

export function CartLibraryTab() {
  const {
    cart: currentCart,
    loadCart,
    createNewCart,
    isDirty,
    saveToCloud,
    cloudSaveEnabled,
    hasUnsavedCloudChanges,
  } = useCart()
  const { user } = useAuth()
  const { openSignIn } = useAuthManager()
  const { success: showSuccess, error: showError, info: showInfo } = useToast()

  const [carts, setCarts] = useState<CartWithStats[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortBy>('updated')
  const [filterBy, setFilterBy] = useState<FilterBy>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    confirmLabel?: string
    confirmVariant?: 'primary' | 'danger'
    onConfirm: () => void
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  })

  // Edit cart modal state
  const [editingCart, setEditingCart] = useState<CartWithStats | null>(null)

  // Cache-aware loading: check cache first, fetch only on miss
  useEffect(() => {
    const cached = CartLibraryCache.get(filterBy, sortBy)
    if (cached) {
      setCarts(cached.data)
      setHasMore(cached.hasMore)
      setTotal(cached.total)
      setLoading(false)
    } else {
      loadCarts(false)
    }
  }, [filterBy, sortBy])

  // Subscribe to cache invalidation to refetch when needed
  useEffect(() => {
    return CartLibraryCache.subscribe(() => {
      loadCarts(false)
    })
  }, [filterBy, sortBy])

  // Clear cache when user actually changes (sign in/out), not on every mount
  const prevUserIdRef = useRef(user?.id)
  useEffect(() => {
    if (prevUserIdRef.current !== user?.id) {
      prevUserIdRef.current = user?.id
      CartLibraryCache.invalidateAll()
    }
  }, [user?.id])

  const loadCarts = async (append: boolean = false) => {
    if (!cloudSaveEnabled) {
      setLoading(false)
      setError(
        'Cloud storage is not configured. Set up Supabase to use the cart library.',
      )
      return
    }

    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setError(null)
      }

      const offset = append ? carts.length : 0
      const result =
        filterBy === 'mine' && user
          ? await CartService.getMyCartsOnly({ sortBy, offset })
          : await CartService.getAllCarts({ sortBy, offset })

      if (append) {
        CartLibraryCache.append(filterBy, sortBy, result)
        setCarts((prev) => [...prev, ...result.data])
      } else {
        CartLibraryCache.set(filterBy, sortBy, result)
        setCarts(result.data)
      }
      setHasMore(result.hasMore)
      setTotal(result.total)
    } catch (err) {
      console.error('Failed to load carts:', err)
      setError('Failed to load carts from cloud storage.')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadCarts(true)
    }
  }

  const showConfirm = (options: {
    title: string
    message: string
    confirmLabel?: string
    confirmVariant?: 'primary' | 'danger'
  }): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmDialog({
        isOpen: true,
        ...options,
        onConfirm: () => {
          resolve(true)
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        },
      })
    })
  }

  const handleLoadCart = useCallback(
    async (cartToLoad: CartWithStats) => {
      // Check if loading the same cart
      if (cartToLoad.id === currentCart.id) {
        loadCart(cartToLoad, cartToLoad.userId)
        showSuccess('Cart reloaded')
        return
      }

      // Check for unsaved local changes
      if (isDirty) {
        const confirmed = await showConfirm({
          title: 'Unsaved Changes',
          message:
            'You have unsaved changes. Loading another cart will discard them. Continue?',
          confirmLabel: 'Discard & Load',
          confirmVariant: 'danger',
        })

        if (!confirmed) return
      }

      // Check for unsaved cloud changes
      if (hasUnsavedCloudChanges) {
        const confirmed = await showConfirm({
          title: 'Unsaved Cloud Changes',
          message:
            'You have changes not saved to the cloud. Loading another cart will discard them. Continue?',
          confirmLabel: 'Discard & Load',
          confirmVariant: 'danger',
        })

        if (!confirmed) return
      }

      try {
        loadCart(cartToLoad, cartToLoad.userId)
        showSuccess(`Loaded "${cartToLoad.metadata.title}"`)
      } catch (err) {
        console.error('Failed to load cart:', err)
        showError('Failed to load cart')
      }
    },
    [
      currentCart.id,
      isDirty,
      hasUnsavedCloudChanges,
      loadCart,
      showSuccess,
      showError,
    ],
  )

  const handleDeleteCart = useCallback(
    async (cartId: string, title: string) => {
      const confirmed = await showConfirm({
        title: 'Delete Cart',
        message: `Are you sure you want to delete "${title}"? This cannot be undone.`,
        confirmLabel: 'Delete',
        confirmVariant: 'danger',
      })

      if (!confirmed) return

      try {
        await CartService.deleteCart(cartId)
        CartLibraryCache.invalidateAll()
        setCarts((prev) => prev.filter((c) => c.id !== cartId))
        if (cartId === currentCart.id) {
          createNewCart()
        }
        showSuccess(`Deleted "${title}"`)
      } catch (err) {
        console.error('Failed to delete cart:', err)
        showError('Failed to delete cart')
      }
    },
    [currentCart.id, createNewCart, showSuccess, showError],
  )

  const handleDuplicateCart = useCallback(
    async (cartId: string) => {
      if (!user) {
        showInfo('Sign in to duplicate carts')
        openSignIn()
        return
      }

      try {
        await CartService.duplicateCart(cartId)
        CartLibraryCache.invalidateAll()
        showSuccess('Cart duplicated')
      } catch (err) {
        console.error('Failed to duplicate cart:', err)
        showError('Failed to duplicate cart')
      }
    },
    [user, showSuccess, showError, showInfo, openSignIn],
  )

  const handleEditCartDetails = useCallback(
    async (cartId: string, newTitle: string, newDescription: string) => {
      try {
        await CartService.updateMetadata(cartId, {
          title: newTitle,
          description: newDescription,
        })
        CartLibraryCache.invalidateAll()
        setCarts((prev) =>
          prev.map((c) =>
            c.id === cartId
              ? {
                  ...c,
                  metadata: {
                    ...c.metadata,
                    title: newTitle,
                    description: newDescription,
                  },
                }
              : c,
          ),
        )
        showSuccess('Cart details updated')
      } catch (err) {
        console.error('Failed to update cart details:', err)
        showError('Failed to update cart details')
      }
    },
    [showSuccess, showError],
  )

  const handleLikeCart = useCallback(
    async (cartId: string, isCurrentlyLiked: boolean) => {
      if (!user) {
        showInfo('Sign in to like carts')
        openSignIn()
        return
      }

      // Optimistic update helper
      const updateLikeState = (liked: boolean) => {
        const updater = (cart: CartWithStats) => ({
          ...cart,
          isLikedByCurrentUser: liked,
          likeCount: liked ? cart.likeCount + 1 : cart.likeCount - 1,
        })
        CartLibraryCache.updateCart(cartId, updater)
        setCarts((prev) => prev.map((c) => (c.id === cartId ? updater(c) : c)))
      }

      // Apply optimistic update
      updateLikeState(!isCurrentlyLiked)

      try {
        await LikeService.toggleLike(cartId, isCurrentlyLiked)
      } catch (err) {
        // Rollback on error
        updateLikeState(isCurrentlyLiked)
        console.error('Failed to like cart:', err)
        showError('Failed to update like')
      }
    },
    [user, showInfo, showError, openSignIn],
  )

  const handleShareCart = useCallback(
    async (cartId: string) => {
      const url = `${window.location.origin}/play/${cartId}`
      try {
        await navigator.clipboard.writeText(url)
        showSuccess('Share link copied to clipboard!')
      } catch {
        showError('Failed to copy link')
      }
    },
    [showSuccess, showError],
  )

  const handleForkCart = useCallback(
    async (cartId: string) => {
      if (!user) {
        showInfo('Sign in to fork carts')
        openSignIn()
        return
      }

      try {
        await CartService.forkCart(cartId)
        CartLibraryCache.invalidateAll()
        showSuccess('Cart forked! Find it in "My Carts"')
      } catch (err) {
        console.error('Failed to fork cart:', err)
        showError('Failed to fork cart')
      }
    },
    [user, showSuccess, showError, showInfo, openSignIn],
  )

  // Filter carts client-side by search (sorting is done server-side)
  const filteredCarts = searchQuery
    ? carts.filter(
        (cart) =>
          cart.metadata.title
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          cart.metadata.author
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          cart.metadata.description
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          cart.username?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : carts

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Cart Library</h2>

        <div className={styles.controls}>
          {/* Filter Tabs - only show if signed in */}
          {user && (
            <div className={styles.filterTabs}>
              <button
                className={`${styles.filterTab} ${filterBy === 'all' ? styles.activeTab : ''}`}
                onClick={() => setFilterBy('all')}
              >
                All Carts
              </button>
              <button
                className={`${styles.filterTab} ${filterBy === 'mine' ? styles.activeTab : ''}`}
                onClick={() => setFilterBy('mine')}
              >
                My Carts
              </button>
            </div>
          )}

          <input
            type='text'
            placeholder='Search carts...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className={styles.sortSelect}
          >
            <option value='updated'>Last Updated</option>
            <option value='created'>Date Created</option>
            <option value='title'>Title</option>
            <option value='likes'>Most Liked</option>
          </select>

          <button
            onClick={() => loadCarts(false)}
            className={styles.refreshButton}
            title='Refresh'
          >
            <RefreshIcon />
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.cartsGrid}>
          {Array.from({ length: 8 }).map((_, i) => (
            <CartCardSkeleton key={i} />
          ))}
        </div>
      ) : error ? (
        <div className={styles.errorState}>
          <p>{error}</p>
          <button
            onClick={() => loadCarts(false)}
            className={styles.retryButton}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {filteredCarts.length === 0 && searchQuery ? (
            <div className={styles.emptyState}>
              <p>No carts match your search.</p>
            </div>
          ) : filteredCarts.length === 0 && filterBy === 'mine' ? (
            <div className={styles.emptyState}>
              <p>You haven't saved any carts yet.</p>
              <p>Save your current cart to see it here!</p>
            </div>
          ) : filteredCarts.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No saved carts yet.</p>
              <p>Save your current cart to see it here!</p>
            </div>
          ) : (
            <>
              <div className={styles.cartsGrid}>
                {filteredCarts.map((cart) => (
                  <CartCard
                    key={cart.id}
                    cart={cart}
                    isActive={cart.id === currentCart.id}
                    isOwner={!!user && cart.userId === user.id}
                    isSignedIn={!!user}
                    onLoad={() => handleLoadCart(cart)}
                    onDelete={() =>
                      handleDeleteCart(cart.id!, cart.metadata.title)
                    }
                    onDuplicate={() => handleDuplicateCart(cart.id!)}
                    onEditDetails={() => setEditingCart(cart)}
                    onLike={() =>
                      handleLikeCart(cart.id!, cart.isLikedByCurrentUser)
                    }
                    onFork={() => handleForkCart(cart.id!)}
                    onShare={() => handleShareCart(cart.id!)}
                  />
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && !searchQuery && (
                <div className={styles.loadMoreContainer}>
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className={styles.loadMoreButton}
                  >
                    {loadingMore
                      ? 'Loading...'
                      : `Load More (${carts.length} of ${total})`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        confirmVariant={confirmDialog.confirmVariant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
      />

      {/* Edit Cart Modal */}
      {editingCart && (
        <EditCartModal
          isOpen={!!editingCart}
          cartId={editingCart.id!}
          currentTitle={editingCart.metadata.title}
          currentDescription={editingCart.metadata.description || ''}
          onSave={handleEditCartDetails}
          onClose={() => setEditingCart(null)}
        />
      )}
    </div>
  )
}
